import { v2Request } from "../helpers/request";

interface Announcement {
  id: number;
  message: string;
  time: string;
  target: string;
  isReadByMe: 0 | 1;

  // ✅ added by /announcements/my-announcements
  isSeenByMe?: 0 | 1;
  isClearedByMe?: 0 | 1;
  emailSent?: 0 | 1;
}

export const markAnnouncementsRead = (announcementIds?: number[]) => {
  const body =
    Array.isArray(announcementIds) && announcementIds.length > 0
      ? { announcementIds }
      : {}; // backend treats missing/empty as "mark all"

  return v2Request("/announcements/mark-read", {
    method: "POST",
    body: JSON.stringify(body),
  });
};

export const createAnnouncement = (payload: JObj) => {
  return v2Request("/createannouncement", {
    body: JSON.stringify(payload),
    method: "POST",
  });
};

export const fetchAllAnnouncements = (
  offset: number,
  rowCount: number,
  isAdmin: boolean,
  isAllAnnouncements: boolean = false,
) => {
  return v2Request(
    `/announcements?offset=${offset}&rowcount=${rowCount}&is_admin=${isAdmin}&is_allAnnouncements=${isAllAnnouncements}`,
  );
};

export type MyAnnouncementsQuery = {
  q?: string;
  from?: string; // YYYY-MM-DD or ISO string
  to?: string; // YYYY-MM-DD or ISO string
  read?: "all" | "unread" | "read";
  cleared?: "exclude" | "include" | "only";
  sort?: "unread" | "date" | "cleared";
  dir?: "asc" | "desc";
  target?: string; // comma-separated, e.g. "REPS_ONLY,BROADCAST"
};

export const fetchMyAnnouncements = (
  page: number = 1,
  limit: number = 5,
  showCleared: boolean = false,
  query: MyAnnouncementsQuery = {},
) => {
  const qp = new URLSearchParams();
  qp.set("page", String(page));
  qp.set("limit", String(limit));
  qp.set("showCleared", showCleared ? "true" : "false");

  if (query.q) qp.set("q", query.q);
  if (query.from) qp.set("from", query.from);
  if (query.to) qp.set("to", query.to);
  if (query.read) qp.set("read", query.read);
  if (query.cleared) qp.set("cleared", query.cleared);
  if (query.sort) qp.set("sort", query.sort);
  if (query.dir) qp.set("dir", query.dir);
  if (query.target) qp.set("target", query.target);

  return v2Request(`/announcements/my-announcements?${qp.toString()}`);
};

export const fetchAdminAnnouncements = (
  page: number = 1,
  limit: number = 10,
) => {
  return v2Request(`/announcements/admin?page=${page}&limit=${limit}`);
};

export const deleteAnnouncement = (id: number) => {
  return v2Request(`/announcements/${id}`, {
    method: "DELETE",
  });
};

export const clearAllAnnouncements = (announcementIds: number[]) => {
  return v2Request("/announcements/clear-all", {
    method: "POST",
    body: JSON.stringify({ announcementIds }),
  });
};

export const setAnnouncementStatus = (payload: {
  announcementId: number;
  isCleared?: boolean | 0 | 1 | null;
  isSeen?: boolean | 0 | 1 | null;
}) => {
  return v2Request("/announcements/set-status", {
    method: "POST",
    body: JSON.stringify(payload),
  });
};

export type { Announcement };

export type AnnouncementSeenBySortBy = "seenAt" | "name" | "email";
export type AnnouncementSeenBySortOrder = "asc" | "desc";
export type AnnouncementSeenByMode = "seen" | "read";

export const fetchAnnouncementReadByUsers = (
  announcementId: number,
  params?: {
    page?: number;
    limit?: number;
    search?: string;
    sortBy?: AnnouncementSeenBySortBy;
    sortOrder?: AnnouncementSeenBySortOrder;
    mode?: AnnouncementSeenByMode;
  },
) => {
  const page = params?.page ?? 1;
  const limit = params?.limit ?? 20;
  const search = (params?.search ?? "").trim();
  const sortBy = params?.sortBy ?? "seenAt";
  const sortOrder = params?.sortOrder ?? "desc";
  const mode = params?.mode ?? "seen";

  const qp = new URLSearchParams();
  qp.set("page", String(page));
  qp.set("limit", String(limit));
  qp.set("sortBy", sortBy);
  qp.set("sortOrder", sortOrder);
  qp.set("mode", mode);
  if (search) qp.set("search", search);

  return v2Request(
    `/announcements/admin/${announcementId}/read-by?${qp.toString()}`,
  );
};
